import React, { useState } from 'react';
import { supabase } from '../utils/supabase';
import { getApiUrl } from '../utils/apiConfig';
import Header from '../components/common/Header';
import PasswordInput from '../components/common/PasswordInput';
import '../styles/SignUp.css';

const SignUp = () => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    retypePassword: '',
    role: 'Student',
    termsAccepted: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [participationChecks, setParticipationChecks] = useState([false, false, false, false, false]);

  const isStudent = formData.role === 'Student';
  const signupUsername = isStudent ? '' : formData.username;

  const signupButtons = [
    { id: 1, label: 'Login', path: '/login' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({ ...prev, role }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.retypePassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!formData.role) {
      setError('Please select your role');
      setLoading(false);
      return;
    }

    if (!formData.termsAccepted) {
      setError('Please accept the Terms & Conditions');
      setLoading(false);
      return;
    }

    try {
      let supabaseUserID = null;
      let supabaseJWT = null;
      // Never reuse a token left over from an earlier signup or session.
      localStorage.removeItem('supabaseJWT');
      
      // Try to handle Supabase authentication to get user ID
      try {
        const { data, error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: window.location.origin + '/email-confirmation?verified=true',
            data: {
              name: formData.name,
              username: signupUsername,
              role: formData.role
            }
          }
        });

        if (error) {
          if (/already (been )?registered|already exists/i.test(error.message)) {
            setError('An account with this email already exists. Please log in instead.');
            return;
          }
          console.warn('Supabase signup error:', error.message);
          console.log('Continuing with backend signup...');
        } else if (data.user && data.user.identities?.length === 0) {
          // With email confirmation on, Supabase hides existing accounts by
          // returning a placeholder user with no identities instead of an error.
          setError('An account with this email already exists. Please log in instead.');
          return;
        } else if (data.user?.id) {
          supabaseUserID = data.user.id;
          console.log('✅ Got Supabase user ID:', supabaseUserID);
          
          // Also try to get the JWT token
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData.session?.access_token) {
            console.log('✅ Got Supabase JWT token');
            supabaseJWT = sessionData.session.access_token;
            localStorage.setItem('supabaseJWT', supabaseJWT);
          }
        } else {
          console.warn('No user ID returned from Supabase');
          console.log('Continuing with backend signup...');
        }
      } catch (supabaseError) {
        console.warn('Supabase signup failed:', supabaseError.message);
        console.log('Continuing with backend signup...');
      }

      // Always send signup data to GoScript backend (with or without Supabase user ID)
      console.log('🔄 Sending signup data to backend...');
      const backendResponse = await fetch(getApiUrl('signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          username: signupUsername,
          email: formData.email,
          password: formData.password,
          retypePassword: formData.retypePassword,
          role: formData.role,
          termsAccepted: formData.termsAccepted,
          supabaseUserId: supabaseUserID, // This will be null if Supabase failed
          supabaseJWT // Only a token from this signup
        })
      });

      if (!backendResponse.ok) {
        const errorData = await backendResponse.json();
        throw new Error(errorData.error || 'Failed to save signup data');
      }

      const backendData = await backendResponse.json();
      console.log('✅ Signup data saved to backend:', backendData);

      // Store the user_id, username, name, and email from backend response for use in profile setup
      localStorage.setItem('signupRole', formData.role);
      localStorage.setItem('signupUserId', backendData.user_id);
      localStorage.setItem('signupUsername', signupUsername);
      localStorage.setItem('signupName', formData.name);
      localStorage.setItem('signupEmail', formData.email);
      console.log('💾 Stored user_id for profile setup:', backendData.user_id);
      console.log('💾 Stored username for profile setup:', signupUsername);
      console.log('💾 Stored name for profile setup:', formData.name);
      console.log('💾 Stored email for profile setup:', formData.email);

      // Redirect to email confirmation regardless of Supabase status
      // The backend has successfully stored the data
      window.location.href = '/email-confirmation';
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const TermsModal = () => (
    <div className="signup-terms-modal-bg">
      <div className="signup-terms-modal">
        <h2 className="signup-terms-title">Terms & Conditions:</h2>
        <ul className="signup-terms-list">
          <li><strong>Acceptance of Terms</strong> </li>
          <p>By registering, accessing, or using this platform (“Service”), users and their parents/guardians agree to comply with these Terms and Conditions (“Terms”), including all related policies. If you do not agree, you must not use the Service.</p>
          <p><strong>To be read and box checked before participation begins:</strong></p>
          <p>I understand that:</p>
          <div className="signup-terms-participation-checks">
            <label className="signup-terms-check-row">
              <input
                type="checkbox"
                checked={participationChecks[0]}
                onChange={() => setParticipationChecks(prev => prev.map((v, i) => i === 0 ? !v : v))}
                className="signup-terms-checkbox"
              />
              <span>I am being asked to help test a learning program</span>
            </label>
            <label className="signup-terms-check-row">
              <input
                type="checkbox"
                checked={participationChecks[1]}
                onChange={() => setParticipationChecks(prev => prev.map((v, i) => i === 1 ? !v : v))}
                className="signup-terms-checkbox"
              />
              <span>A camera will record me while I use the program but will not collect or store any recordings of video or images of me.</span>
            </label>
            <label className="signup-terms-check-row">
              <input
                type="checkbox"
                checked={participationChecks[2]}
                onChange={() => setParticipationChecks(prev => prev.map((v, i) => i === 2 ? !v : v))}
                className="signup-terms-checkbox"
              />
              <span>I can stop anytime if I don&apos;t want to continue</span>
            </label>
            <label className="signup-terms-check-row">
              <input
                type="checkbox"
                checked={participationChecks[3]}
                onChange={() => setParticipationChecks(prev => prev.map((v, i) => i === 3 ? !v : v))}
                className="signup-terms-checkbox"
              />
              <span>I can ask questions if I don&apos;t understand something</span>
            </label>
            <label className="signup-terms-check-row">
              <input
                type="checkbox"
                checked={participationChecks[4]}
                onChange={() => setParticipationChecks(prev => prev.map((v, i) => i === 4 ? !v : v))}
                className="signup-terms-checkbox"
              />
              <span>My parents/guardians have said it&apos;s okay for me to participate in the testing of the proof of concept called "In The Zone (ITZ)".</span>
            </label>
          </div>
          <br/>

          <li><strong>User Eligibility</strong></li>
          <p>The Service is intended for students with ADHD, learning differences, parents/guardians, and educators. But, we will welcome all student testers for feedback on our Proof of Concept (POC).</p>
          <p> <b>1.</b> Users under 18 require verifiable parental or guardian consent before accessing the Service. Click link to fill out the PDF.</p>
          <p> <b>2.</b> For users under 13, the platform complies with the Children’s Online Privacy Protection Act (COPPA) and will not be collecting personal data.</p><br/>
          
          <li><strong>Parental Consent</strong> </li>
          <p> <b>1.</b> Verifiable parental consent will be obtained via electronic signature prior to account creation for minors under 18.</p>
          <p> <b>2.</b> Parents/guardians have ongoing control over their minor’s data, including the ability to review, correct, or request deletion of personal information.</p>
          <p> <b>3.</b> Parents can revoke consent and request account termination at any time.</p><br/>
          
          <li><strong>Account Registration and Personal Information</strong></li>
          <p> <b>1.</b> Users must provide accurate information including name, age, and email. Additional data regarding learning needs may be requested to personalize education content.</p>
          <p> <b>2.</b> Parents/guardians of minors will manage consent and account access.</p><br/>
          
          <li><strong>Use of Computer Vision and AI Technologies</strong></li>
          <p> <b>1.</b> The Service uses computer vision (e.g., device cameras) only to monitor user engagement, support personalized learning, and ensure platform integrity. No video or images are stored without explicit knowledge.</p>
          <p> <b>2.</b> Any real-time imagery or data collected is encrypted, used solely for enhancing learning, and is not used for biometric profiling or shared outside Service operation.</p>
          <p> <b>3.</b> AI agents adapt content based on user interaction; these AI outputs are continuously monitored for fairness and accuracy, with human oversight available.</p> <br/>
          
          <li><strong>Device Tracking and IP Address Usage</strong></li>
          <p> The Service may collect users’ device information and IP addresses to:</p><br/>
          <p> <b>1.</b> Authenticate login sessions and secure accounts</p>
          <p> <b>2.</b> Detect and prevent fraudulent access</p>
          <p> <b>3.</b> Optimize learning experiences by matching features per device type</p> <br/>
          <p>Device and IP data will be used only for operational and security purposes, and not for third-party marketing or profiling.</p><br/>
          
          <li><strong>Data Collection, Privacy, and Security</strong></li>
          <p> <b>1.</b> The Service collects only necessary personal data, usage analytics, and device information, exclusively to personalize experiences and maintain security.</p>
          <p> <b>2.</b> All data is encrypted during transmission and storage.</p>
          <p><b>3.</b> The platform complies with relevant privacy laws, including GDPR, CCPA, FERPA, and COPPA.</p>
          <p> <b>4.</b> Parents/guardians have rights to access, correct, and delete their children’s data, and to opt out of non-essential data collection where feasible.</p>
          <p> <b>5.</b> Personal data will not be sold or used for marketing purposes. Limited sharing with trusted third-party cloud providers is solely for Service operation.</p><br/>
          
          <li><strong>Use Restrictions</strong></li>
          <p>Users (and parents) may not:</p> <br/>
          <p> <b>1.</b> Share or publish other users’ data without permission</p>
          <p> <b>2.</b> Attempt unauthorized system access</p>
          <p> <b>3.</b> Violate intellectual property rights</p><br/>

          <li><strong>Safeguarding Vulnerable Users</strong></li>
          <p> <b>1.</b> The Service includes moderation features and safeguards against harassment or exploitation.</p>
          <p> <b>2.</b> Violation of policies may lead to suspension or termination.</p>
          <p> <b>3.</b> Reasonable accommodations are provided for neurodiverse learners</p><br/>

          <li><strong>Intellectual Property</strong></li>
          <p> All content, AI models, and educational materials remain the property of the platform provider or licensors and may not be copied or redistributed except as explicitly allowed.</p><br/>

          <li><strong>Disclaimer and Liability</strong></li>
          <p> <b>1.</b> The Service is provided “as is.” While designed for educational support, the platform does not guarantee specific outcomes.</p>
          <p> <b>2.</b> Liability for damages is limited as allowed by law.</p><br/>

          <li><strong>Changes to Terms</strong></li>
          <p> The provider may update Terms; continued use signals acceptance of changes. Users and parents will be notified of material changes.</p><br/>

          <li><strong>Contact and Complaints</strong></li>
          <p> <b>1.</b> Support is available via email and help center <b><u>help@inthezoneapp.org</u></b></p>
          <p> <b>2.</b> Parents and educators have direct contact channels for privacy and safety concerns.</p> <br/>
        </ul>
        <div className="signup-terms-ack">
          <h3 className="signup-terms-ack-title">Additional Notes for Users, parents, and teachers</h3>
          <p>These Terms are designed with special consideration for the privacy, protection, and equitable support of neurodiverse learners, while meeting legal and operational requirements for technologies like computer vision, AI, and device/IP tracking in modern edtech platforms.</p>
          <p><b>Follow these steps to make sure you understand the rules and can participate safely.</b></p>
          <p><b>1.</b> Click the <strong>‘Close’</strong> button below to confirm you have read, understood, and accepted the terms.</p>
          <p><b>2.</b> Next, find the page titled <strong>“Welcome to your Journey!”</strong> Check the box labeled <strong>‘Terms & Conditions’</strong></p><br/>
          <p><b>3.</b> Last, click the <strong>‘Submit’</strong> button to complete the process.</p>
        </div>
        <button
          onClick={() => setShowTerms(false)}
          className="signup-terms-close"
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <div className="signup-page">
      <Header buttons={signupButtons} />

      <div className="signup-main">
        <div className="signup-header">
          <p className="signup-title">
            Welcome to the journey!
          </p> <br/>
          <p className="signup-desc">
            As an ITZ member, you’ll join our special beta community - ITZ BETAs. 
            You’ll get early access to exciting new tools we’re creating and a chance to share your thoughts directly with us. 
            We’re here to listen and support your every step of the way, helping you stay focused, build confidence, and reach your academic goals—today and beyond.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          {/* Left Column */}
          <div>
            {error && error !== 'Passwords do not match' && error !== 'Please select your role' && error !== 'Please accept the Terms & Conditions' && (
              <div className="signup-error">
              {error}
              </div>
            )}

            <div className="signup-form-group">
              <label className="signup-label">
                Name (first & last):
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            {/* Students choose their username during profile setup */}
            {!isStudent && (
              <div className="signup-form-group">
                <label className="signup-label">
                  Username:
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  className="signup-input"
                />
              </div>
            )}

            <div className="signup-form-group">
              <label className="signup-label">
                Email Address:
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <div className="signup-form-group">
              <label className="signup-label">
                Are you a...
              </label>
              <div style={{ display: 'flex', gap: '30px' }}>
                {['Student', 'Parent', 'Teacher'].map((role) => (
                  <label key={role} className="signup-role-label">
                    <input
                      type="checkbox"
                      name="role"
                      value={role}
                      checked={formData.role === role}
                      onChange={() => handleRoleChange(role)}
                      className="signup-radio"
                    />
                    {role}
                  </label>
                ))}
              </div>
              {error === 'Please select your role' && (
                <div className="signup-error">
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div className="signup-form-group">
              <label className="signup-label">
                New Password 
                <label className="signup-label-remember"> (Remember your password)</label>
              </label> 
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="signup-input"
                wrapClassName="signup-password-wrap"
              />
            </div>

            <div className="signup-form-group">
              <label className="signup-label">
                Retype New Password
              </label>
              <PasswordInput
                name="retypePassword"
                value={formData.retypePassword}
                onChange={handleInputChange}
                required
                className={`signup-input${formData.retypePassword && formData.retypePassword !== formData.password ? ' error' : ''}`}
                wrapClassName="signup-password-wrap"
              />
              {formData.retypePassword && formData.retypePassword !== formData.password && (
                <p className="signup-error">Passwords do not match - try again.</p>
              )}
            </div>

            <div className="signup-terms-group">
              <label className="signup-terms-label">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleInputChange}
                  className="signup-checkbox"
                />
                Please read{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  className="signup-terms-link"
                >
                  Terms & Conditions
                </button> {' '}and return to this page to check this box
              </label>
              {error === 'Please accept the Terms & Conditions' && (
                <div className="signup-error">
                  {error}
                </div>
              )}
            </div>

            <div className="signup-submit-row">
              <button 
                type="submit" 
                disabled={loading}
                className="signup-submit app-btn"
              >
                {loading ? 'Loading...' : 'Submit'}
              </button>
              <div className="signup-email-verification">
                <span className="email-verification-italic">Email Verification</span><br />
                <span>Required</span>
              </div>
            </div>
          </div>
        </form>
      </div>

      {showTerms && <TermsModal />}
    </div>
  );
};

export default SignUp;